import React, { useEffect, useState } from 'react';
import { 
    Checkbox,
    DefaultButton, 
    IconButton,
    Modal,
    Panel, 
    PanelType, 
    TextField, 
    IPanelStyles,
    IButtonStyles,
    mergeStyleSets,
} from '@fluentui/react';
import LoaderComponent from '../loader/loader.component';
import ErrorMessageComponent from '../error-message/error-message.component';
import CustomerCardComponent, { ICustomerCardContactFields } from '../customer-card/customer-card.component';

import ApiService from '../../services/api/api.service';

import ItemCliente from '../../models/ItemCliente';
import ItemDireccion from '../../models/ItemDireccion';
import ItemFormulario from '../../models/ItemFormulario';
import ItemOrdenDeTrabajo from '../../models/ItemOrdenDeTrabajo';
import ItemTarea from '../../models/ItemTarea';
import useAuthStatus from '../../hooks/auth/AuthHook';

const panelStyles : IPanelStyles = {
    root: {},
    overlay: {},
    hiddenPanel: {},
    main: {},
    commands: {},
    contentInner: {},
    scrollableContent: {},
    navigation: {},
    header: {},
    headerText: {},
    content: {},
    footer: {},
    footerInner: {},
    subComponentStyles: {
        closeButton: {
            root: { color: 'rgb(50, 49, 48)' },
        },
    },
};

const sectionClasses = mergeStyleSets({
    title: {
        display: 'block',
        backgroundColor: 'var(--color-primario)',
        color: 'white',
        padding: '4px 12px',
        fontWeight: 600,
        fontSize: '13px',
        marginBottom: '8px',
        marginTop: '12px',
    },
});

const modalClasses = mergeStyleSets({
    container: { padding: '20px', minWidth: '400px', maxWidth: '600px' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    thead: { backgroundColor: 'var(--color-primario)', color: 'white' },
    th: { padding: '6px 10px', textAlign: 'left' },
    thNarrow: { padding: '6px 10px', textAlign: 'left', width: '80px' },
    rowEven: { backgroundColor: '#fff', cursor: 'pointer' },
    rowOdd: { backgroundColor: '#f5f5f5', cursor: 'pointer' },
    td: { padding: '6px 10px' },
    empty: { padding: '10px', textAlign: 'center', color: '#666' },
});

const iconButtonStyles: IButtonStyles = {
    root: {
        backgroundColor: 'white',
        color: 'var(--color-primario)',
        border: '1px solid var(--color-primario)',
        height: '32px',
        width: '32px',
        selectors: {
            span: { selectors: { i: { color: 'var(--color-primario)' } } },
        },
    },
    rootHovered: {
        backgroundColor: 'var(--color-primario)',
        borderColor: 'var(--color-primario)',
        selectors: {
            span: { selectors: { i: { color: '#ffffff' } } },
        },
    },
};

const saveButtonStyles: IButtonStyles = {
    root: {
        backgroundColor: 'var(--color-primario)',
        color: 'white',
        borderColor: 'var(--color-primario)',
        borderRadius: 6,
        padding: '0 20px',
        height: 36,
    },
    rootHovered: {
        backgroundColor: 'white',
        color: 'var(--color-primario)',
        borderColor: 'var(--color-primario)',
    },
    label: { fontWeight: 600 },
    icon: { color: 'inherit' },
};

interface IWorkCreateComponentProps {
    workOrder: ItemOrdenDeTrabajo;
    onClose: () => void;
    onWorkCreated: (item: ItemTarea) => void;
};

const WorkCreateComponent = (props: IWorkCreateComponentProps) => {
    const auth = useAuthStatus();

    const [formularios, setFormularios] = useState<ItemFormulario[]>([]);
    const [formularioNombre, setFormularioNombre] = useState<string>('');
    const [isFormulariosOpen, setIsFormulariosOpen] = useState<boolean>(false);
    const [ventanaHoraria, setVentanaHoraria] = useState<boolean>(false);
    const [work, setWork] = useState<ItemTarea>(() => {
        const now = new Date().toISOString().substring(0, 16);
        const t = new ItemTarea();
        t.IdOrden = props.workOrder.Id;
        t.Nombre = props.workOrder.Nombre;
        t.Direccion = props.workOrder.Direccion;
        t.CPostal = props.workOrder.CPostal;
        t.Poblacion = props.workOrder.Poblacion;
        t.Provincia = props.workOrder.Provincia;
        t.Pais = props.workOrder.Pais;
        t.ControlDeTiempos = 1;
        t.FechaHoraInicioPrevista = now;
        t.FechaPrevistaFin = now;
        return t;
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [addresses, setAddresses] = useState<ItemDireccion[]>([]);

    useEffect(() => {
        if (auth.user) {
            ApiService.Post<ItemFormulario[]>('OrdenesTrabajo', 'GetFormularios', auth.user, { Filtro: '' })
                .then((r: ItemFormulario[]) => setFormularios(r), () => {});

            // Cargar direcciones del cliente de la orden automáticamente
            if (props.workOrder.CodCliente) {
                ApiService.Post<ItemDireccion[]>('OrdenesTrabajo', 'GetCliente', auth.user, { CodCliente: String(props.workOrder.CodCliente) })
                    .then((res: ItemDireccion[]) => setAddresses(res), () => setAddresses([]));
            }
        }
    }, [auth.user, props.workOrder.CodCliente]);

    const onFormularioBuscar = (): void => {
        setIsFormulariosOpen(true);
    };

    const onFormularioSelected = (item: ItemFormulario): void => {
        setWork(w => ({ ...w, Formulario: String(item.Id), FormularioDatos: item.EstructuraFormulario }));
        setFormularioNombre(item.Descripcion);
        setIsFormulariosOpen(false);
    };

    const onCustomerSelected = (item: ItemCliente): void => {
        setAddresses([]);
        setWork(w => ({
            ...w,
            CodCliente: parseInt(item.CodigoCliente) || 0,
            Nombre: '',
            Direccion: '',
            CPostal: '',
            Poblacion: '',
            Provincia: '',
            Pais: '',
        }));
        if (auth.user && item.CodigoCliente) {
            ApiService.Post<ItemDireccion[]>('OrdenesTrabajo', 'GetCliente', auth.user, { CodCliente: item.CodigoCliente })
                .then((res: ItemDireccion[]) => {
                    setAddresses(res);
                    if (res.length > 0) {
                        const first = res[0];
                        setWork(w => ({
                            ...w,
                            CodCliente: parseInt(first.CodCliente) || w.CodCliente,
                            CodDireccion: parseInt(first.CodigoDireccion) || 0,
                            Nombre: first.Nombre || '',
                            Direccion: first.Direccion || '',
                            CPostal: first.CPostal || '',
                            Poblacion: first.Poblacion || '',
                            Provincia: first.Provincia || '',
                            Pais: first.Pais || '',
                        }));
                    }
                }, () => { setAddresses([]); });
        }
    };

    const onAddressChanged = (fields: ICustomerCardContactFields): void => {
        setWork(w => ({
            ...w,
            Nombre: fields.Nombre,
            Direccion: fields.Direccion,
            CPostal: fields.CPostal,
            Poblacion: fields.Poblacion,
            Provincia: fields.Provincia,
            Pais: fields.Pais,
            PosicionGPS: fields.PosicionGPS,
        }));
    };

    const onSubmit = (): void => {
        if (auth.user) {
            setLoading(true);
            setError('');
            const payload = {
                ...work,
                Orden: work.Orden || 0,
                FechaHoraInicioPrevista: work.FechaHoraInicioPrevista || null,
                FechaPrevistaFin: work.FechaPrevistaFin || null,
                FechaEnvio: work.FechaEnvio || null,
                FechaEntrega: work.FechaEntrega || null,
                FechaFinalizacion: work.FechaFinalizacion || null,
                ControlDeTiempos: String(work.ControlDeTiempos),
                CodDireccion: work.CodDireccion ? String(work.CodDireccion) : "",
                CodCliente: work.CodCliente ? String(work.CodCliente) : "",
                TiempoPrevisto: work.TiempoPrevisto || 0,
                PEContacto: work.PEContacto || false,
                PEDireccion: work.PEDireccion || false,
                Referencia: work.Referencia || "",
                VHInicio: ventanaHoraria ? (work.VHInicio || null) : null,
                VHFinal: ventanaHoraria ? (work.VHFinal || null) : null,
            };
            ApiService.Post<number>("OrdenesTrabajo", "SetTrabajo", auth.user, {
                "ItemTareas": payload,
            }).then((response: number) => {
                const created = { ...work, Id: response };
                props.onWorkCreated(created);
                props.onClose();
                setLoading(false);
            }, (err: string) => {
                setError(err);
                setLoading(false);
            });
        }
    };

    const addressDisplayText = [work.Direccion, work.CPostal, work.Poblacion, work.Provincia]
        .filter(Boolean)
        .join('  ');

    return (
        <>
        <Panel 
            headerText='Nueva actuación'
            isOpen={true}
            onDismiss={() => { if (!isFormulariosOpen) props.onClose(); }}
            type={PanelType.largeFixed}
            styles={panelStyles}>
            <div className="container-fluid p-0">
                <div className='row m-0'>

                    {/* ── IDENTIFICACIÓN ── */}
                    <div className="col-12 p-0">
                        <span className={sectionClasses.title}>Identificación</span>
                    </div>
                    <div className="col-12 p-0 mb-2">
                        <CustomerCardComponent
                            label='Dirección de la actuación'
                            nombre={work.Nombre}
                            direccion={work.Direccion}
                            cpostal={work.CPostal}
                            poblacion={work.Poblacion}
                            provincia={work.Provincia}
                            pais={work.Pais}
                            telefono=""
                            email=""
                            web=""
                            posicionGPS={work.PosicionGPS}
                            displayText={addressDisplayText}
                            addressList={addresses}
                            onCustomerSelected={onCustomerSelected}
                            onContactChanged={onAddressChanged} />
                    </div>
                    <div className="col-12 p-0 mb-2">
                        <TextField
                            label='Referencia'
                            value={work.Referencia}
                            onChange={(_e, v) => setWork(w => ({ ...w, Referencia: v ?? "" }))}
                            placeholder="(opcional)" />
                    </div>

                    {/* PLANIFICACION */}
                    <div className="col-12 p-0">
                        <span className={sectionClasses.title}>Planificacion</span>
                    </div>
                    <div className="col-12 p-0 mb-2">
                        <div className="row m-0">
                            <div className="col-8 ps-0 pe-2">
                                <TextField
                                    label='Fecha y hora prevista de inicio'
                                    type='datetime-local'
                                    value={(work.FechaHoraInicioPrevista || '').substring(0, 16)}
                                    onChange={(_e, v) => setWork(w => ({ ...w, FechaHoraInicioPrevista: (v ?? '').substring(0, 16) }))} />
                            </div>
                            <div className="col-4 px-0">
                                <TextField
                                    label='Tiempo previsto (min.)'
                                    type='number'
                                    min='0'
                                    step='0.1'
                                    value={String(work.TiempoPrevisto)}
                                    onChange={(_e, v) => setWork(w => ({ ...w, TiempoPrevisto: parseFloat(v ?? "0") || 0 }))} />
                            </div>
                        </div>
                    </div>
                    <div className="col-12 p-0 mb-2">
                        <TextField
                            label='Fecha prevista de fin'
                            type='datetime-local'
                            value={(work.FechaPrevistaFin || '').substring(0, 16)}
                            onChange={(_e, v) => setWork(w => ({ ...w, FechaPrevistaFin: (v ?? '').substring(0, 16) }))} />
                    </div>

                    {/* DESCRIPCION DE LA ACTUACION */}
                    <div className="col-12 p-0">
                        <span className={sectionClasses.title}>Descripcion de la actuacion</span>
                    </div>
                    <div className="col-12 p-0 mb-1">
                        <Checkbox
                            label="Permitir al usuario modificar en la App los tiempos reales de las tareas"
                            checked={work.ControlDeTiempos === 1}
                            onChange={(_e, c) => setWork(w => ({ ...w, ControlDeTiempos: c ? 1 : 0 }))} />
                    </div>
                    <div className="col-12 p-0 mb-1">
                        <Checkbox
                            label="Enviar Informe de Actuacion al 'contacto'"
                            checked={work.PEContacto}
                            disabled={true} />
                    </div>
                    <div className="col-12 p-0 mb-2">
                        <Checkbox
                            label="Enviar Informe de Actuacion a la 'direccion de actuacion'"
                            checked={work.PEDireccion}
                            disabled={true} />
                    </div>
                    <div className="col-12 p-0 mb-2">
                        <div className="row m-0 align-items-end">
                            <div className="col p-0">
                                <TextField
                                    label='Formulario (opcional)'
                                    readOnly={true}
                                    value={formularioNombre || 'No hay formulario adjunto.'}
                                    styles={{ field: { cursor: 'default' } }} />
                            </div>
                            <div className="col-auto p-0 ms-1">
                                <IconButton
                                    iconProps={{ iconName: 'Search' }}
                                    styles={iconButtonStyles}
                                    title='Buscar formulario'
                                    onClick={onFormularioBuscar} />
                            </div>
                        </div>
                    </div>
                    <div className="col-12 p-0 mb-2">
                        <Checkbox
                            label="Ventana horaria"
                            checked={ventanaHoraria}
                            onChange={(_e, c) => setVentanaHoraria(c ?? false)} />
                    </div>
                    {ventanaHoraria && (
                        <div className="col-12 p-0 mb-2">
                            <div className="row m-0">
                                <div className="col-6 ps-0 pe-2">
                                    <TextField
                                        label='Hora inicio'
                                        type='time'
                                        value={work.VHInicio}
                                        onChange={(_e, v) => setWork(w => ({ ...w, VHInicio: v ?? "" }))} />
                                </div>
                                <div className="col-6 px-0">
                                    <TextField
                                        label='Hora fin'
                                        type='time'
                                        value={work.VHFinal}
                                        onChange={(_e, v) => setWork(w => ({ ...w, VHFinal: v ?? "" }))} />
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="col-12 p-0 mb-2">
                        <TextField
                            label='Asunto'
                            value={work.Descripcion}
                            onChange={(_e, v) => setWork(w => ({ ...w, Descripcion: v ?? "" }))} />
                    </div>
                    <div className="col-12 p-0 mb-2">
                        <TextField
                            label='Descripcion (opcional)'
                            value={work.Observaciones}
                            onChange={(_e, v) => setWork(w => ({ ...w, Observaciones: v ?? "" }))}
                            multiline
                            rows={3} />
                    </div>

                    <div className="col-12 py-2 px-0">
                        <DefaultButton 
                            text='Crear trabajo'
                            iconProps={{ iconName: 'Add' }}
                            styles={saveButtonStyles}
                            onClick={onSubmit} />
                    </div>
                </div>
            </div>
            <ErrorMessageComponent message={error} />
            <LoaderComponent loading={loading} />
        </Panel>

        <Modal
            isOpen={isFormulariosOpen}
            onDismiss={() => setIsFormulariosOpen(false)}
            isBlocking={true}>
            <div className={modalClasses.container}>
                <div className={modalClasses.header}>
                    <strong>Seleccionar formulario</strong>
                    <IconButton iconProps={{ iconName: 'Cancel' }} onClick={() => setIsFormulariosOpen(false)} />
                </div>
                <table className={modalClasses.table}>
                    <thead>
                        <tr className={modalClasses.thead}>
                            <th className={modalClasses.th}>Descripcion</th>
                            <th className={modalClasses.thNarrow}>Version</th>
                        </tr>
                    </thead>
                    <tbody>
                        {formularios.map((f, i) => (
                            <tr
                                key={f.Id}
                                className={i % 2 === 0 ? modalClasses.rowEven : modalClasses.rowOdd}
                                onClick={() => onFormularioSelected(f)}>
                                <td className={modalClasses.td}>{f.Descripcion}</td>
                                <td className={modalClasses.td}>{f.Version}</td>
                            </tr>
                        ))}
                        {formularios.length === 0 && (
                            <tr><td colSpan={2} className={modalClasses.empty}>No hay formularios disponibles</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </Modal>
        </>
    );
};

export default WorkCreateComponent;
